import React from 'react';
import { Navigate, Route, Link, useLocation } from 'react-router-dom';
import BlogIndexPage from './pages/marketing/BlogIndexPage';
import BlogPostPage from './pages/marketing/BlogPostPage';
import CareersPage from './pages/marketing/CareersPage';
import JobDetailPage from './pages/marketing/JobDetailPage';
import CaseStudiesIndexPage from './pages/marketing/CaseStudiesIndexPage';
import CaseStudyDetailPage from './pages/marketing/CaseStudyDetailPage';
import AboutPage from './pages/marketing/AboutPage';
import PrivacyPolicyPage from './pages/marketing/PrivacyPolicyPage';
import TermsOfServicePage from './pages/marketing/TermsOfServicePage';
import MoneyBackGuaranteePage from './pages/marketing/MoneyBackGuaranteePage';
import DemoPage from './pages/marketing/DemoPage';
import AiContentCalendarPage from './pages/marketing/AiContentCalendarPage';
import AiAutomationsLandingPage from './pages/marketing/AiAutomationsLandingPage';
import ClientsWinningPage from './pages/marketing/ClientsWinningPage';
import ConsultationPage from './pages/marketing/ConsultationPage';
import MarketingShell from './components/MarketingShell';
import AccountStatementGuidePage from './pages/AccountStatementGuidePage';

const PricingPageNew = React.lazy(() => import('./pages/PricingPageNew'));

function SimplePage({ title, description, to, cta }) {
  return (
    <MarketingShell title={title} description={description}>
      {to ? (
        <a href={to} className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors">
          {cta || 'Continue'}
        </a>
      ) : (
        <p className="text-sm text-gray-600">This page has moved into the product. Use the navigation to continue.</p>
      )}
    </MarketingShell>
  );
}

function HelpCenterPage() {
  const location = useLocation();
  return (
    <MarketingShell
      title="Help center"
      description="Guides for statements, success fees, and getting started."
    >
      <div className="space-y-3">
        <Link
          to={{ pathname: "/help/how-success-fees-are-calculated", search: location.search }}
          className="block rounded-xl border border-gray-100 bg-white p-6 hover:border-gray-200 hover:shadow-sm transition-all duration-150"
        >
          <p className="text-sm font-semibold text-gray-900">
            How success fees are calculated
          </p>
          <p className="mt-1 text-sm text-gray-600">
            Due today, coming due later, payment terms, and how your monthly
            account statement is built.
          </p>
        </Link>
        <div className="rounded-xl border border-gray-100 bg-white p-6 text-sm text-gray-600">
          For other product questions, email hello@getupgraded.ai or use in-app
          Intercom once you sign in.
        </div>
      </div>
    </MarketingShell>
  );
}

const SIGNED_IN_SKIP = new Set(['home-blog', 'plans', 'pricing', 'ai-auto']);

export const publicMarketingRoutes = [
  <Route key="blog" path="/blog" element={<BlogIndexPage />} />,
  <Route key="home-blog" path="/home" element={<Navigate to="/blog" replace />} />,
  <Route key="post" path="/post/:slug" element={<BlogPostPage />} />,
  <Route key="blog-cats" path="/blog-categories/:slug" element={<Navigate to="/blog" replace />} />,
  <Route key="tags" path="/tags/:slug" element={<Navigate to="/blog" replace />} />,
  <Route key="careers" path="/careers" element={<CareersPage />} />,
  <Route key="careers-1" path="/careers-1" element={<Navigate to="/careers" replace />} />,
  <Route key="careers-2" path="/careers-2" element={<Navigate to="/careers" replace />} />,
  <Route key="jobs" path="/jobs/:slug" element={<JobDetailPage />} />,
  <Route key="case-studies" path="/case-studies" element={<CaseStudiesIndexPage />} />,
  <Route key="case-study" path="/case-studies/:slug" element={<CaseStudyDetailPage />} />,
  <Route key="customer-stories" path="/customer-stories" element={<Navigate to="/case-studies" replace />} />,
  <Route key="about" path="/about" element={<AboutPage />} />,
  <Route key="about-1" path="/about-1" element={<Navigate to="/about" replace />} />,
  <Route key="about-2" path="/about-2" element={<Navigate to="/about" replace />} />,
  <Route key="about-3" path="/about-3" element={<Navigate to="/about" replace />} />,
  <Route key="team" path="/team" element={<Navigate to="/about" replace />} />,
  <Route key="team-slug" path="/team/:slug" element={<Navigate to="/about" replace />} />,
  <Route key="privacy" path="/privacy-policy" element={<PrivacyPolicyPage />} />,
  <Route key="privacy-short" path="/privacy" element={<Navigate to="/privacy-policy" replace />} />,
  <Route key="terms" path="/terms-of-service" element={<TermsOfServicePage />} />,
  <Route key="legal" path="/legal" element={<Navigate to="/terms-of-service" replace />} />,
  <Route key="mbg" path="/moneyback-guarantee" element={<MoneyBackGuaranteePage />} />,
  <Route key="demo" path="/demo" element={<DemoPage />} />,
  <Route key="inbox" path="/ai-inbox" element={<Navigate to="/" replace />} />,
  <Route key="calendar" path="/ai-content-calendar" element={<AiContentCalendarPage />} />,
  <Route key="ai-auto" path="/ai-automations" element={<AiAutomationsLandingPage />} />,
  <Route key="clients" path="/clients-winning" element={<ClientsWinningPage />} />,
  <Route
    key="consult"
    path="/free-savings-consultation"
    element={<ConsultationPage title="Free savings consultation" description="Book time with an advisor to review grants, credits, and vendor spend." />}
  />,
  <Route
    key="consult-dmg"
    path="/free-savings-consultation-dmg-partnership"
    element={<ConsultationPage title="DMG partnership consultation" description="Exclusive savings consultation for the DMG network." />}
  />,
  <Route
    key="sss"
    path="/savings-strategy-session"
    element={<ConsultationPage title="Savings strategy session" description="Walk through a personalized earnings plan." />}
  />,
  <Route
    key="ssc"
    path="/savings-strategy-consultation"
    element={<ConsultationPage title="Savings strategy consultation" description="A focused review of programs and savings you already qualify for." />}
  />,
  <Route
    key="estimate"
    path="/get-your-savings-estimate"
    element={<ConsultationPage title="Get your savings estimate" description="Tell us about the company and we will follow up with an estimate." />}
  />,
  <Route key="partner" path="/partner-with-upgraded" element={<ConsultationPage title="Partner with Upgraded" description="Accountants, CFOs, and firms: build a referral or delivery partnership." />} />,
  <Route key="dmg-offer" path="/dmg-exclusive-offer" element={<ConsultationPage title="DMG exclusive offer" description="Partner pricing and onboarding for DMG members." />} />,
  <Route key="dmg-signup" path="/dmg-sign-up" element={<Navigate to="/sign-up" replace />} />,
  <Route key="help" path="/help-center" element={<HelpCenterPage />} />,
  <Route
    key="statement-guide"
    path="/help/how-success-fees-are-calculated"
    element={<AccountStatementGuidePage />}
  />,
  <Route key="help-cat" path="/article-category/:slug" element={<HelpCenterPage />} />,
  <Route key="help-tag" path="/help-center-tags/:slug" element={<HelpCenterPage />} />,
  <Route key="grantgpt" path="/grantgpt" element={<SimplePage title="GrantGPT" description="Grant discovery and drafting now live in the product." to="/all-grants" cta="Browse grants" />} />,
  <Route key="gov" path="/government-funding" element={<Navigate to="/all-grants" replace />} />,
  <Route key="gov-copy" path="/government-funding-copy" element={<Navigate to="/all-grants" replace />} />,
  <Route key="newsletter" path="/newsletter" element={<ConsultationPage title="Newsletter" description="Get nearly-free-money updates. Email hello@getupgraded.ai to subscribe." />} />,
  <Route key="changelog" path="/changelog" element={<SimplePage title="Changelog" description="Product updates now ship inside the app." to="/" cta="Go to home" />} />,
  <Route key="plans" path="/plans" element={<React.Suspense fallback={null}><PricingPageNew /></React.Suspense>} />,
  <Route key="pricing" path="/pricing" element={<React.Suspense fallback={null}><PricingPageNew /></React.Suspense>} />,
  <Route key="antler" path="/antler-community" element={<ConsultationPage title="Antler community" description="Special onboarding for Antler founders." />} />,
  <Route key="future" path="/future-skool" element={<ConsultationPage title="Future Skool" description="Community offer — book a walkthrough." />} />,
  <Route key="summit" path="/web-summit-100-000-in-savings-raffle" element={<SimplePage title="Web Summit raffle" description="This campaign has ended. Book a session to see current savings." to="/demo" cta="Book a session" />} />,
  <Route key="cao" path="/chief-automation-officer" element={<Navigate to="/ai-automations" replace />} />,
  <Route key="sops" path="/top-101-sops" element={<Navigate to="/ai-automations" replace />} />,
  <Route key="autos" path="/top-50-automations-checklist" element={<Navigate to="/ai-automations" replace />} />,
  <Route key="resources" path="/free-funding-resources" element={<Navigate to="/blog" replace />} />,
  <Route key="sample" path="/sample-savings-estimate" element={<Navigate to="/get-your-savings-estimate" replace />} />,
];

export const signedInMarketingRoutes = publicMarketingRoutes.filter(
  (route) => !SIGNED_IN_SKIP.has(route.key)
);
